import {
  TKioskCostCentreOption,
  TKioskPlanningDropdownItem,
  TKioskPoliciesByLanguage,
  TKioskPolicy,
  TKioskStartRegistrationData,
  TKioskStartRegistrationWarning,
} from 'types/kiosk/TKioskStartRegistrationData';
import { asArray, asRecords, toNumberOrNull, toStringOrNull } from './mapper-utils';

const EMPTY: TKioskStartRegistrationData = {
  planningDropdownItems: [],
  selectedDate: null,
  selectedCostCentreId: null,
  selectedStartWork: null,
  selectedEndWork: null,
  allowRegistrationBeforePlannedStart: true,
  warnings: [],
  costCentres: [],
  nearestCostCentreId: null,
  policies: [],
  policiesByLanguage: {},
};

const toCostCentres = (rows: unknown[]): TKioskCostCentreOption[] =>
  asRecords(rows)
    .filter(r => r.value !== undefined && r.value !== null)
    .map(r => ({ value: Number(r.value), label: String(r.label ?? '') }));

const toPlanningItems = (rows: unknown[]): TKioskPlanningDropdownItem[] =>
  asRecords(rows)
    .filter(r => Boolean(r.contractDateId))
    .map(r => ({
      contractDateId: Number(r.contractDateId),
      label: String(r.label ?? ''),
      date: String(r.date ?? ''),
      startWork: String(r.startWork ?? ''),
      endWork: String(r.endWork ?? ''),
      costCenterId: Number(r.costCenterId ?? 0), // American spelling, per backend
      companyId: Number(r.companyId ?? 0),
      availableCostCentres: toCostCentres(asArray(r.availableCostCentres)),
    }));

const toPolicies = (rows: unknown[]): TKioskPolicy[] =>
  asRecords(rows).map(r => ({ key: String(r.key ?? ''), label: String(r.label ?? '') }));

/** `{ nl: [...], fr: [...], en: [...] }` → the same shape with mapped policies; anything else → `{}`. */
export const toKioskPoliciesByLanguage = (value: unknown): TKioskPoliciesByLanguage => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([language, rows]) => [language, toPolicies(asArray(rows))]),
  );
};

const toWarnings = (rows: unknown[]): TKioskStartRegistrationWarning[] =>
  asRecords(rows).map(r => ({
    validationError: String(r.validationError ?? ''),
    errorDetails: toStringOrNull(r.errorDetails),
    contractDateId: toNumberOrNull(r.contractDateId),
  }));

const toAllowRegistrationBeforePlannedStart = (body: Record<string, unknown>): boolean => {
  if (typeof body.allowRegistrationBeforePlannedStart === 'boolean') return body.allowRegistrationBeforePlannedStart;
  if (typeof body.disableRegistrationBeforePlanningStart === 'boolean')
    return !body.disableRegistrationBeforePlanningStart;
  return true;
};

/** Maps the raw StartRegistrationData body (treated as unknown) to a typed shape.
 *  A malformed/empty body returns the empty default; collections are never null. */
export const fromResponseToKioskStartRegistrationData = (response: unknown): TKioskStartRegistrationData => {
  if (typeof response !== 'object' || response === null) return EMPTY;
  const body = response as Record<string, unknown>;
  return {
    planningDropdownItems: toPlanningItems(asArray(body.planningDropdownItems)),
    selectedDate: toStringOrNull(body.selectedDate),
    selectedCostCentreId: toNumberOrNull(body.selectedCostCentreId),
    selectedStartWork: toStringOrNull(body.selectedStartWork),
    selectedEndWork: toStringOrNull(body.selectedEndWork),
    allowRegistrationBeforePlannedStart: toAllowRegistrationBeforePlannedStart(body),
    warnings: toWarnings(asArray(body.warnings)),
    costCentres: toCostCentres(asArray(body.costCentres)),
    nearestCostCentreId: toNumberOrNull(body.nearestCostCentreId),
    policies: toPolicies(asArray(body.policies)),
    policiesByLanguage: toKioskPoliciesByLanguage(body.policiesByLanguage),
  };
};
